import { useState, useRef, useCallback, useEffect } from "react";
import { Mic, MicOff } from "lucide-react";

interface Props {
  onRecordingComplete: (base64: string, mimeType: string) => void;
  disabled?: boolean;
}

export default function VoiceRecorder({ onRecordingComplete, disabled }: Props) {
  const [recording, setRecording] = useState(false);
  const [volume, setVolume] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number>(0);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    setRecording(false);
    setVolume(0);
  }, []);

  const startRecording = useCallback(async () => {
    try {
      chunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Volume analyser for visual feedback
      const ctx = new AudioContext();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      analyserRef.current = analyser;

      const tick = () => {
        const data = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        setVolume(Math.min(avg / 128, 1));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();

      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";

      const mr = new MediaRecorder(stream, { mimeType: mime });

      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        const reader = new FileReader();
        reader.onloadend = () => {
          const b64 = (reader.result as string).split(",")[1];
          onRecordingComplete(b64, mime);
        };
        reader.readAsDataURL(blob);
        ctx.close();
      };

      mr.start(250);
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch {
      setRecording(false);
    }
  }, [onRecordingComplete]);

  // Spacebar handlers
  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      if (e.code === "Space" && !disabled && !recording) {
        e.preventDefault();
        startRecording();
      }
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.code === "Space" && recording) {
        e.preventDefault();
        stopRecording();
      }
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
    };
  }, [recording, disabled, startRecording, stopRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const ringScale = 1 + volume * 0.15;

  return (
    <div className="flex flex-col items-center gap-3">
      <button
        onMouseDown={recording ? undefined : startRecording}
        onMouseUp={recording ? stopRecording : undefined}
        onMouseLeave={recording ? stopRecording : undefined}
        disabled={disabled}
        className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-all select-none ${
          recording
            ? "bg-red-500 shadow-lg shadow-red-500/40"
            : disabled
              ? "bg-gray-300 dark:bg-navy-700 cursor-not-allowed"
              : "bg-citilux-blue-600 hover:bg-citilux-blue-500 shadow-lg shadow-citilux-blue-500/30 cursor-pointer active:scale-95"
        }`}
      >
        {/* Pulse ring */}
        {recording && (
          <span
            className="absolute inset-0 rounded-full bg-red-500/30 animate-ping"
            style={{ transform: `scale(${ringScale})` }}
          />
        )}
        {recording ? (
          <MicOff className="w-8 h-8 text-white relative z-10" />
        ) : (
          <Mic className="w-8 h-8 text-white relative z-10" />
        )}
      </button>

      <p className="text-sm font-medium text-gray-500 dark:text-metallic-silver select-none">
        {recording
          ? "Recording... release to stop"
          : disabled
            ? "Processing..."
            : "Hold Space or click & hold to speak"}
      </p>
    </div>
  );
}
